import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';

import type {
  AgendaEvent,
  ConflictItem,
  EditScope,
  EventCategory,
  EventDetail,
  EventInput,
  EventPatch,
  EventPreview,
  Catalog,
  EventTemplate,
  MemberIds,
  OccurrencePatch,
  RecurrenceInput,
  RolesInput,
} from '@amumin/api-client';
import type { Weekday } from '@amumin/domain/recurrence';
import { t } from '@amumin/i18n';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  DurationChips,
  FieldPair,
  Notice,
  RadioList,
  Select,
  showToast,
  TextField,
  TimeField,
} from '@amumin/ui';
import { cx, Icon, Pictogram, PICTO_GROUPS } from '@amumin/ui-core';

import { api, errorMessage, isApiError } from '../api.js';
import {
  formatDuration,
  hhmmOf,
  joinNames,
  membersOf,
  minutesOf,
  type MemberLite,
} from '../lib/agenda.js';
import {
  addDays,
  daysBetween,
  formatDateKeyLong,
  formatDateKeyShort,
  isValidDateKey,
  isValidHhmm,
  weekdayOf,
} from '../lib/dates.js';
import {
  defaultRepeat,
  repeatFromInput,
  repeatSummary,
  repeatToInput,
  sameRepeat,
  weekdayCode,
  type RepeatForm,
  type RepeatKind,
} from '../lib/recurrenceForm.js';
import {
  draftsFromDetail,
  effectiveReminders,
  remindersInput,
  sameReminders,
  type ReminderDraft,
} from '../lib/reminders.js';
import { hiddenPictoIds, usePictoCatalog } from '../lib/pictoCatalog.js';
import {
  daysFromDetail,
  formWeekdays,
  rideDaysChanged,
  settleRideDays,
  ridesInput,
  type RideDays,
} from '../lib/rides.js';
import { suggestPicto, templateIcon } from '../lib/suggest.js';
import { mutate, readPref, toastError, writePref } from '../lib/ui.js';
import { moduleOn } from '../state/modules.js';
import {
  appText,
  bumpData,
  context,
  handleAuthLoss,
  locale,
  messages,
  refreshApprovals,
} from '../state/session.js';
import { ScopeDialog } from './Dialogs.js';
import { HiddenOptions } from './HiddenOptions.js';
import { MemberPicker } from './MemberPicker.js';
import { PictoField } from './PictoField.js';
import { FixedActivityEditor, type FixedActivityHandle } from './FixedActivityEditor.js';
import { PlaceField, type PlaceFieldHandle } from './PlaceField.js';
import { ReminderEditor, type ReminderFlush } from './ReminderEditor.js';
import { endAfterStartChange, validDuration } from '../lib/timeRange.js';
import { RidesEditor } from './RidesEditor.js';

// Afspraak-editor (DESIGN.md §8.W3, ONTWERP CAL-10..27): één sheet voor nieuw en bewerken.
// Eerst het belangrijkste (wat, voor wie, wanneer, herhalen); brengen/halen, reistijd, waar en
// notitie onder "Meer opties". Live NL-samenvatting en conflicten vóór het opslaan. Bij een
// herhaling vraagt Opslaan altijd "alleen deze keer / deze en volgende / de hele reeks".

export type EditorTarget =
  | { kind: 'new'; date: string; start?: string; memberIds?: MemberIds }
  | { kind: 'edit'; occurrence: AgendaEvent };

type RoleChoice = 'none' | 'open' | string;

interface Form {
  title: string;
  icon: string | null;
  category: EventCategory | null;
  member_ids: MemberIds;
  date: string;
  allDay: boolean;
  start: string;
  end: string;
  endDate: string;
  repeat: RepeatForm;
  dropoff: RoleChoice;
  pickup: RoleChoice;
  dropoffOffset: number;
  pickupOffset: number;
  /** 0022: "Per dag anders" (null = elke keer dezelfde). */
  rideDays: RideDays | null;
  /**
   * Reistijd in minuten; 0 = "Geen reistijd" (de ouder koos dat; wint van de berekende tijd);
   * null = automatisch (de berekende tijd van de gekozen plek, of geen als er geen plek is).
   */
  travel: number | null;
  /** 0056: dezelfde reistijd ook ná de afspraak (de weg terug). */
  travelBack: boolean;
  alert: number;
  location: string;
  notes: string;
  reminders: ReminderDraft[];
}

/** Standaardduur van een nieuwe afspraak (minuten). */
const NEW_DURATION = 60;
const WEEKDAY_ORDER: Weekday[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const AVATAR_IDS = PICTO_GROUPS.find((g) => g.id === 'avatar')?.ids ?? [];
const TRAVEL_OPTIONS = [0, 5, 10, 15, 20, 30, 45, 60];
const ALERT_OPTIONS = [0, 5, 10, 15, 30];
/** Voorgesteld seintje als een ouder bij een nieuwe afspraak een plek kiest (minuten vooraf). */
const DEFAULT_PLACE_ALERT = 15;

/** Reistijdkeuzes plus de huidige waarde (bijvoorbeeld 25 min, uitgerekend via een plek). */
function travelOptions(current: number | null): number[] {
  return current === null || TRAVEL_OPTIONS.indexOf(current) >= 0
    ? TRAVEL_OPTIONS
    : [...TRAVEL_OPTIONS, current].sort((x, y) => x - y);
}

/** Waarde van de keuzelijst Reistijd voor "automatisch" (de berekende tijd van de plek). */
const TRAVEL_AUTO = 'auto';
/** Oprichter 2 okt: "ik wil ook handmatig reistijd kunnen toevoegen" (eigen waarde in minuten). */
const TRAVEL_OWN = 'own';
/** CreateEvent.travel_minutes en de DB-check (0005): 0 tot en met 1440. */
const TRAVEL_MAX = 1440;

/**
 * Reistijd van deze keer voor het formulier (review g3-fs r1): de reeks bewaart null =
 * automatisch; de agenda vult die al aan met de berekende tijd, dus daar lezen we hem niet. Heeft
 * deze ene keer een eigen reistijd ("alleen deze"), dan die (null daar = geen reistijd).
 */
function travelOfDetail(d: EventDetail, occ: AgendaEvent): number | null {
  const own = d.overrides.find((o) => o.occurrence_id === occ.occurrence_id);
  if (own && Object.prototype.hasOwnProperty.call(own.payload, 'travel_minutes')) {
    const v = own.payload.travel_minutes;
    return typeof v === 'number' ? v : 0;
  }
  return d.travel_minutes;
}

function roleChoice(role: { member_id: string | null } | null | undefined): RoleChoice {
  if (!role) return 'none';
  return role.member_id ?? 'open';
}

/** Nieuwe afspraak: een lege lijst per dag hoeft niet mee. */
function withoutEmptyDays(r: ReturnType<typeof ridesInput>): ReturnType<typeof ridesInput> {
  return r.role_weekdays && r.role_weekdays.length === 0 ? { roles: r.roles } : r;
}

function durationOf(form: Form): number {
  return minutesOf(form.end) - minutesOf(form.start);
}

function newForm(date: string, start: string, memberIds: MemberIds): Form {
  const s = isValidHhmm(start) ? start : '09:00';
  return {
    title: '',
    icon: null,
    category: null,
    member_ids: memberIds,
    date,
    allDay: false,
    start: s,
    end: hhmmOf(Math.min(minutesOf(s) + NEW_DURATION, 23 * 60 + 59)),
    endDate: date,
    repeat: defaultRepeat(date),
    dropoff: 'none',
    pickup: 'none',
    dropoffOffset: 0,
    pickupOffset: 0,
    rideDays: null,
    travel: null,
    travelBack: false,
    alert: 0,
    location: '',
    notes: '',
    reminders: [],
  };
}

/** Formulier uit de serie (EventDetail) + de aangeklikte keer (datum/tijd/leden van die keer). */
function formFromDetail(d: EventDetail, occ: AgendaEvent): Form {
  const date = occ.date ?? d.date;
  const start = occ.all_day
    ? (d.start_hhmm ?? '09:00')
    : (occ.start_hhmm ?? d.start_hhmm ?? '09:00');
  const duration = occ.duration_min ?? d.duration_min ?? 60;
  const form: Form = {
    title: occ.title,
    icon: occ.icon,
    category: d.category,
    member_ids: occ.member_ids,
    date,
    allDay: occ.all_day,
    start,
    end: occ.all_day
      ? hhmmOf(minutesOf(start) + 60)
      : occ.end_hhmm && occ.end_hhmm !== '24:00'
        ? occ.end_hhmm
        : hhmmOf(minutesOf(start) + duration),
    endDate: d.end_date ?? date,
    repeat: repeatFromInput(d.recurrence, d.date),
    dropoff: roleChoice(occ.roles.find((r) => r.role === 'dropoff') ?? d.roles.dropoff),
    pickup: roleChoice(occ.roles.find((r) => r.role === 'pickup') ?? d.roles.pickup),
    dropoffOffset: d.roles.dropoff?.offset_minutes ?? 0,
    pickupOffset: d.roles.pickup?.offset_minutes ?? 0,
    rideDays: null,
    travel: travelOfDetail(d, occ),
    travelBack: occ.travel_return ?? d.travel_return ?? false,
    alert: occ.departure_alert_minutes ?? d.departure_alert_minutes ?? 0,
    location: occ.location ?? '',
    notes: occ.notes ?? d.notes ?? '',
    reminders: draftsFromDetail(d.reminders),
  };
  // 0022: met rijders per weekdag toont de editor de verdeling van de reeks (niet deze keer).
  const rideDays = daysFromDetail(d, formWeekdays(form));
  return rideDays
    ? settleRideDays({
        ...form,
        dropoff: roleChoice(d.roles.dropoff),
        pickup: roleChoice(d.roles.pickup),
        rideDays,
      })
    : form;
}

function toInput(form: Form): EventInput {
  const input: EventInput = {
    title: form.title.trim(),
    date: form.date,
    all_day: form.allDay,
    member_ids: form.member_ids,
    icon: form.icon,
    location: form.location.trim() || null,
    notes: form.notes.trim() || null,
    category: form.category,
    // 0 = "Geen reistijd" blijft 0 (de berekende tijd vult alleen null aan); null = automatisch.
    travel_minutes: form.travel,
    travel_return: form.travel === 0 ? false : form.travelBack,
    departure_alert_minutes: form.alert > 0 ? form.alert : null,
    recurrence: repeatToInput(form.repeat, form.date),
    ...withoutEmptyDays(ridesInput(form)),
    reminders: form.allDay
      ? []
      : remindersInput(effectiveReminders(form.reminders, form.member_ids, null).drafts),
  };
  if (form.allDay) {
    if (form.endDate > form.date) input.end_date = form.endDate;
  } else {
    input.start_hhmm = form.start;
    input.duration_min = durationOf(form);
  }
  return input;
}

function sameIds(a: MemberIds, b: MemberIds): boolean {
  if (a === 'everyone' || b === 'everyone') return a === b;
  return a.length === b.length && a.every((x) => b.includes(x));
}

function sameRoles(a: RolesInput, b: RolesInput): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function EventEditor({
  target,
  members,
  onClose,
}: {
  target: EditorTarget | null;
  members: ReadonlyArray<MemberLite>;
  onClose: () => void;
}) {
  const m = messages.value;
  const a = appText.value;
  const open = target !== null;
  const editing = target?.kind === 'edit' ? target.occurrence : null;
  // Golf 2 r2 (blocker): de Dialog blijft gemount (ook dicht), dus onderdelen met eigen staat
  // (het open herinneringsformulier met zijn checklist, de vaste activiteit) bleven over van de
  // vorige afspraak en gingen stil mee in de volgende. Elke keer openen = een nieuwe sleutel voor
  // het formulier, al bij de eerste render met het nieuwe doel (vóór focus en effecten).
  const lastTarget = useRef<EditorTarget | null>(null);
  const openKey = useRef(0);
  if (target && target !== lastTarget.current) {
    lastTarget.current = target;
    openKey.current += 1;
  }

  const form = useSignal<Form>(newForm('2026-01-01', '09:00', []));
  const initial = useSignal<Form | null>(null);
  const detail = useSignal<EventDetail | null>(null);
  const loading = useSignal(false);
  const errors = useSignal<Record<string, string | undefined>>({});
  const notice = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);
  const more = useSignal(false);
  // Golf 2 (oprichter #11): uitgeklapt tot volledig scherm, onthouden per gebruiker (deze browser).
  // Wisselen verandert alleen de vorm: het formulier blijft staan, er gaat niets verloren.
  const fullKey = `fb.event-editor.full.${context.value?.identity.id ?? 'anon'}`;
  const full = useSignal(readPref(fullKey, ['1', '0'] as const, '0') === '1');
  const toggleFull = () => {
    full.value = !full.value;
    writePref(fullKey, full.value ? '1' : '0');
  };
  // Ronde 1: in het volledige scherm is een Escape per ongeluk duur. Staat er getypte tekst die
  // nog niet is opgeslagen (titel, plaats, notitie) of een gewijzigde vaste activiteit, dan eerst
  // 'Wijzigingen weggooien?'. Annuleren blijft direct (bewuste keuze).
  const typed = useRef('');
  const textOf = (f: Form) => JSON.stringify([f.title, f.location, f.notes]);
  const discardOpen = useSignal(false);
  const requestClose = () => {
    const dirty = textOf(form.value) !== typed.current || fixedRef.current?.dirty() === true;
    if (full.value && dirty) {
      discardOpen.value = true;
      return;
    }
    onClose();
  };
  // Pictogram-voorstel uit de titel zolang de ouder zelf nog niets koos.
  const iconTouched = useSignal(false);
  /** Reistijd: het veld "eigen tijd" staat open. */
  const travelOwn = useSignal(false);
  const templates = useSignal<EventTemplate[]>([]);
  /** 0024: ingebouwde sjablonen die bij dit gezin uit het zicht staan (met reden). */
  const hiddenTemplates = useSignal<Catalog<EventTemplate>['hidden']>([]);
  const hiddenBadge = useSignal(0);
  const pictoCatalog = usePictoCatalog('general');
  const preview = useSignal<EventPreview | null>(null);
  const scopeOpen = useSignal(false);
  const orphanScope = useSignal<EditScope | null>(null);
  const orphanChoice = useSignal<'keep_as_event' | 'delete'>('keep_as_event');
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reminderFlush = useRef<(() => ReminderFlush) | null>(null);
  // 0031: vaste activiteit met pauzes (eigen blok; bewaart na het opslaan van de afspraak).
  const fixedRef = useRef<FixedActivityHandle | null>(null);
  // 0034: de gekozen plek (Plaatsen zoeken) gaat na de afspraak mee, net als de vaste activiteit.
  const placeRef = useRef<PlaceFieldHandle | null>(null);
  // De berekende reistijd van de gekozen plek (voor "Automatisch (30 min)"); null = geen of nog niet.
  const placeAuto = useSignal<{ chosen: boolean; minutes: number | null }>({
    chosen: false,
    minutes: null,
  });
  // Review g3-ux r1: kiest een ouder bij een nieuwe afspraak een plek, dan stelt de editor één keer
  // een seintje voor (15 min vooraf); anders kwam de vertrekbanner nooit op het bord.
  const alertProposed = useRef(false);
  const onPlaceAuto = (x: { chosen: boolean; minutes: number | null }) => {
    placeAuto.value = x;
    if (
      x.chosen &&
      !editing &&
      !alertProposed.current &&
      form.value.alert === 0 &&
      moduleOn('travel_time')
    ) {
      alertProposed.current = true;
      form.value = { ...form.value, alert: DEFAULT_PLACE_ALERT };
    }
  };
  const commitFixed = async (seriesId: string): Promise<void> => {
    try {
      await fixedRef.current?.commit(seriesId);
    } catch {
      showToast({ kind: 'error', message: a.activities.saveFailed });
    }
    try {
      if (moduleOn('place_search')) await placeRef.current?.commit(seriesId);
    } catch {
      showToast({ kind: 'error', message: a.places.saveFailed });
    }
  };
  // Bedoelde duur Van–Tot: alleen uit wat de ouder koos (Tot, duurknop, sjabloon) of wat er al
  // stond, nooit uit een ingekort paar (review c-reg/c-ux r3: 23:00–23:59 maakte elke volgende
  // Van + 59 min). Blijft over een typfout in Van heen. endClamped: Tot is ingekort tot 23:59.
  const lastDuration = useRef<number | null>(null);
  const endClamped = useSignal(false);

  // Openen: formulier vullen (nieuw: defaults; bewerken: serie ophalen).
  useEffect(() => {
    if (!target) return;
    errors.value = {};
    notice.value = undefined;
    preview.value = null;
    busy.value = false;
    endClamped.value = false;
    lastDuration.current = null;
    if (target.kind === 'new') {
      const f = newForm(target.date, target.start ?? '09:00', target.memberIds ?? 'everyone');
      form.value = f;
      typed.current = textOf(f);
      discardOpen.value = false;
      // Een nieuwe afspraak duurt een uur, ook als het begin (23:00) Tot op 23:59 zet.
      lastDuration.current = NEW_DURATION;
      initial.value = null;
      detail.value = null;
      more.value = false;
      iconTouched.value = false;
      api.app.eventTemplates
        .list()
        .then((res) => {
          templates.value = res.items;
          hiddenTemplates.value = res.hidden ?? [];
          hiddenBadge.value = res.badge ?? 0;
        })
        .catch(() => undefined);
      return;
    }
    loading.value = true;
    api.app.events
      .get(target.occurrence.series_id)
      .then((d) => {
        detail.value = d;
        const f = formFromDetail(d, target.occurrence);
        form.value = f;
        typed.current = textOf(f);
        discardOpen.value = false;
        lastDuration.current = validDuration(f.start, f.end);
        initial.value = f;
        iconTouched.value = true;
        more.value =
          f.dropoff !== 'none' ||
          f.pickup !== 'none' ||
          (f.travel ?? 0) > 0 ||
          !!f.notes ||
          !!f.location;
      })
      .catch((e: unknown) => {
        if (!handleAuthLoss(e)) notice.value = errorMessage(e, m);
      })
      .finally(() => (loading.value = false));
  }, [target]);

  // Release-c r2: herhaalt een nieuwe afspraak op twee of meer dagen, dan gaat "Meer opties" één
  // keer vanzelf open, zodat "Brengen en halen" met "Per dag anders" te vinden is.
  const multiDay = target?.kind === 'new' && formWeekdays(form.value).length >= 2;
  useEffect(() => {
    if (multiDay) more.value = true;
  }, [multiDay]);

  const patch = (changes: Partial<Form>) => {
    const prev = form.value;
    const next = { ...prev, ...changes };
    // Datum verschoven bij een wekelijkse herhaling op één dag: die dag schuift mee.
    if (changes.date && changes.date !== prev.date && isValidDateKey(changes.date)) {
      const oldDay = weekdayCode(prev.date);
      const r = next.repeat;
      if (r.weekdays.length === 1 && r.weekdays[0] === oldDay) {
        next.repeat = { ...r, weekdays: [weekdayCode(changes.date)] };
      }
      if (next.endDate < changes.date) next.endDate = changes.date;
    }
    // Minder dan twee weekdagen over: "Per dag anders" wordt de eenvoudige keuze (rides.ts).
    form.value = settleRideDays(next);
    schedulePreview();
  };
  const patchRepeat = (changes: Partial<RepeatForm>) =>
    patch({ repeat: { ...form.value.repeat, ...changes } });

  const validate = (): boolean => {
    const f = form.value;
    const e: Record<string, string | undefined> = {};
    if (!f.title.trim()) e.title = a.editor.titleRequired;
    else if (f.title.trim().length > 120) e.title = m.web.titleTooLong;
    if (!isValidDateKey(f.date)) e.date = m.web.dateInvalid;
    if (f.member_ids !== 'everyone' && f.member_ids.length === 0)
      e.member_ids = a.editor.memberRequired;
    if (!f.allDay) {
      if (!isValidHhmm(f.start)) e.start = m.web.startInvalid;
      else if (!isValidHhmm(f.end)) e.end = a.timePicker.invalid;
      else if (durationOf(f) <= 0) e.end = a.editor.endBeforeStart;
    }
    errors.value = e;
    return Object.values(e).every((x) => !x);
  };

  /** Een door de ouder gekozen geldig paar wordt de duur die blijft bij het schuiven van Van. */
  const keepDuration = (start: string, end: string) => {
    const d = validDuration(start, end);
    if (d !== null) lastDuration.current = d;
  };

  /**
   * Een rode fout onder Van of Tot (na een mislukte Opslaan) verdwijnt zodra het veld weer klopt,
   * niet pas bij de volgende Opslaan (review c-fs r3). Tot hangt van Van af: die kijken we samen na.
   */
  const clearTimeError = (field: 'start' | 'end', value: string) => {
    const e = errors.value;
    if (!e.start && !e.end) return;
    const next = { ...e };
    const fm = form.value;
    if (field === 'start' && isValidHhmm(value)) next.start = undefined;
    if (e.end && isValidHhmm(fm.start) && isValidHhmm(fm.end) && durationOf(fm) > 0)
      next.end = undefined;
    errors.value = next;
  };

  function schedulePreview() {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => void runPreview(), 450);
  }

  async function runPreview() {
    const f = form.value;
    if (!f.title.trim() || !isValidDateKey(f.date)) return;
    if (f.member_ids !== 'everyone' && f.member_ids.length === 0) return;
    if (!f.allDay && (!isValidHhmm(f.start) || durationOf(f) <= 0)) return;
    try {
      preview.value = await api.app.events.preview(toInput(f));
    } catch {
      preview.value = null;
    }
  }

  useEffect(() => {
    if (open && !loading.value) schedulePreview();
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [open, loading.value]);

  // ---- Opslaan ------------------------------------------------------------------------------

  const repeatChanged = (): boolean => {
    const d = detail.value;
    if (!d) return false;
    const now = repeatToInput(form.value.repeat, form.value.date);
    const before: RecurrenceInput | null = d.recurrence
      ? {
          rrule: d.recurrence.rrule,
          ...(d.recurrence.count ? { count: d.recurrence.count } : {}),
          ...(d.recurrence.until ? { until: d.recurrence.until } : {}),
          ...(d.recurrence.week_parity ? { week_parity: d.recurrence.week_parity } : {}),
        }
      : null;
    // Het formulier normaliseert (BYDAY op volgorde); vergelijk via dezelfde weg.
    const normalizedBefore = before
      ? repeatToInput(repeatFromInput(d.recurrence, d.date), d.date)
      : null;
    return !sameRepeat(now, normalizedBefore) && !sameRepeat(now, before);
  };

  /**
   * "Voor wie" bij een herinnering (release-b r1): een herinnering hoort bij de reeks. Wijzigde de
   * ouder de leden hier niet, dan de leden van de reeks (niet die van deze ene keer); anders de
   * nieuwe leden (die gaan dan met de reeks mee).
   */
  const reminderChoices = (): MemberIds | null => {
    const d = detail.value;
    const i = initial.value;
    if (!d || !i || !d.recurrence || !sameIds(form.value.member_ids, i.member_ids)) return null;
    if (d.member_ids !== 'everyone') return d.member_ids;
    if (d.except_member_ids.length === 0) return 'everyone';
    return members.map((x) => x.id).filter((id) => !d.except_member_ids.includes(id));
  };

  /** Alleen gewijzigde velden (PATCH): minder kans op onbedoeld overschrijven. */
  const changedFields = (): EventPatch => {
    const f = form.value;
    const i = initial.value;
    const input = toInput(f);
    if (!i) return input;
    const out: EventPatch = {};
    if (f.title.trim() !== i.title.trim()) out.title = input.title;
    if (f.icon !== i.icon) out.icon = f.icon;
    if (!sameIds(f.member_ids, i.member_ids)) out.member_ids = f.member_ids;
    if (f.date !== i.date) out.date = f.date;
    if (f.allDay !== i.allDay) out.all_day = f.allDay;
    if (!f.allDay && (f.start !== i.start || f.allDay !== i.allDay)) out.start_hhmm = f.start;
    if (!f.allDay && (durationOf(f) !== durationOf(i) || f.allDay !== i.allDay))
      out.duration_min = durationOf(f);
    if (f.allDay && f.endDate !== i.endDate) out.end_date = f.endDate > f.date ? f.endDate : null;
    if (f.location.trim() !== i.location.trim()) out.location = input.location ?? null;
    if (f.notes.trim() !== i.notes.trim()) out.notes = input.notes ?? null;
    if (f.travel !== i.travel) out.travel_minutes = input.travel_minutes ?? null;
    if (input.travel_return !== (i.travel === 0 ? false : i.travelBack))
      out.travel_return = input.travel_return ?? false;
    if (f.alert !== i.alert) out.departure_alert_minutes = input.departure_alert_minutes ?? null;
    const rf = ridesInput(f);
    if (!sameRoles(rf.roles, ridesInput(i).roles)) out.roles = rf.roles;
    if (rideDaysChanged(f, i)) out.role_weekdays = rf.role_weekdays ?? [];
    if (repeatChanged()) out.recurrence = input.recurrence ?? null;
    // Bijsnoeien alleen bij een ledenwijziging in dit formulier (review r2); bij "Alleen deze"
    // gaan herinneringen nooit mee (de presenter slaat een kind dat niet meedoet zelf over).
    const rem = effectiveReminders(f.reminders, f.member_ids, i.member_ids).drafts;
    if (!sameReminders(rem, i.reminders)) out.reminders = f.allDay ? [] : remindersInput(rem);
    return out;
  };

  const create = async () => {
    const input = toInput(form.value);
    busy.value = true;
    // "Ongedaan maken" haalt de net gemaakte afspraak weer weg (prullenbak).
    const ref: { id: string; version: number } = { id: '', version: 0 };
    const res = await mutate(() => api.app.events.create(input), {
      success: t(a.editor.created, { title: input.title }),
      undo: () => api.app.events.remove(ref.id, ref.version),
    });
    if (res) {
      ref.id = res.id;
      ref.version = res.version;
      await commitFixed(res.id);
      busy.value = false;
      onClose();
      return;
    }
    busy.value = false;
  };

  const saveWithScope = async (scope: EditScope, orphanAction?: 'keep_as_event' | 'delete') => {
    const occ = editing;
    const d = detail.value;
    if (!occ || !d) return;
    const changes = changedFields();
    busy.value = true;
    try {
      if (scope === 'this') {
        const body: OccurrencePatch = {};
        if (changes.title !== undefined) body.title = changes.title;
        if (changes.date !== undefined && changes.date !== null) body.date = changes.date;
        if (changes.start_hhmm) body.start_hhmm = changes.start_hhmm;
        if (changes.all_day !== undefined) body.all_day = changes.all_day;
        if (changes.duration_min !== undefined) body.duration_min = changes.duration_min;
        if (changes.member_ids !== undefined) body.member_ids = changes.member_ids;
        if (changes.icon !== undefined) body.icon = changes.icon;
        if (changes.location !== undefined) body.location = changes.location;
        if (changes.notes !== undefined) body.notes = changes.notes;
        if (changes.travel_minutes !== undefined) body.travel_minutes = changes.travel_minutes;
        if (changes.travel_return !== undefined) body.travel_return = changes.travel_return;
        if (changes.departure_alert_minutes !== undefined)
          body.departure_alert_minutes = changes.departure_alert_minutes;
        if (changes.roles !== undefined) body.roles = changes.roles;
        body.if_version = occ.override_version ?? 0;
        await api.app.occurrences.update(occ.occurrence_id, body);
      } else {
        // `date` + `occurrence_id` = de nieuwe datum van déze keer (de API verschuift de serie
        // met hetzelfde aantal dagen). Een eerder verplaatste keer staat op een andere dag dan
        // zijn oorspronkelijke slot: stuur de verschuiving relatief aan dat slot.
        const seriesChanges = { ...changes };
        if (seriesChanges.date !== undefined && seriesChanges.date !== null) {
          const shownDate = occ.date ?? d.date;
          const shift = daysBetween(shownDate, seriesChanges.date);
          seriesChanges.date = addDays(occ.original_date ?? shownDate, shift);
        }
        await api.app.events.update(occ.series_id, {
          ...seriesChanges,
          scope,
          occurrence_id: occ.occurrence_id,
          ...(orphanAction ? { orphan_action: orphanAction } : {}),
          if_version: d.version,
        });
      }
      scopeOpen.value = false;
      orphanScope.value = null;
      await commitFixed(occ.series_id);
      bumpData();
      void refreshApprovals();
      showToast({ kind: 'success', message: a.editor.saved });
      onClose();
    } catch (e) {
      scopeOpen.value = false;
      if (isApiError(e, 409, 'orphans_pending')) {
        orphanScope.value = scope;
      } else if (isApiError(e, 409)) {
        notice.value = errorMessage(e, m);
        // Nieuwste versie ophalen zodat een tweede poging kan slagen.
        api.app.events
          .get(occ.series_id)
          .then((fresh) => (detail.value = fresh))
          .catch(() => undefined);
      } else if (isApiError(e, 400)) {
        notice.value = errorMessage(e, m);
      } else toastError(e);
    } finally {
      busy.value = false;
    }
  };

  const submit = async () => {
    // Een open herinneringsformulier gaat mee (of houdt opslaan tegen bij een fout).
    const flushed = reminderFlush.current ? reminderFlush.current() : null;
    if (flushed && !flushed.ok) return;
    if (flushed) form.value = { ...form.value, reminders: flushed.list };
    if (!validate()) return;
    if (!editing) {
      await create();
      return;
    }
    const changes = changedFields();
    if (Object.keys(changes).length === 0) {
      // Alleen de vaste activiteit of de plek gewijzigd: die apart bewaren (geen bereikvraag).
      if (fixedRef.current?.dirty() || (moduleOn('place_search') && placeRef.current?.dirty())) {
        busy.value = true;
        await commitFixed(editing.series_id);
        busy.value = false;
        bumpData();
        showToast({ kind: 'success', message: a.activities.saved });
      }
      onClose();
      return;
    }
    if (editing.recurring) {
      scopeOpen.value = true;
      return;
    }
    await saveWithScope('all');
  };

  // ---- Weergave -----------------------------------------------------------------------------

  const f = form.value;
  // "Automatisch" alleen met een gekozen plek (zonder plek is er niets uit te rekenen).
  const autoTravelShown = moduleOn('place_search') && placeAuto.value.chosen;
  const summary =
    f.repeat.kind === 'none'
      ? null
      : repeatSummary(
          f.repeat,
          f.date,
          { allDay: f.allDay, start: f.start, end: f.end },
          locale.value,
        );
  const conflicts: ConflictItem[] = (preview.value?.conflicts ?? []).filter(
    (c) =>
      !editing || (c.series_id !== editing.series_id && c.occurrence_id !== editing.occurrence_id),
  );
  const repeatOptions: Array<{ value: RepeatKind; label: string }> = [
    { value: 'none', label: a.editor.repeatNone },
    { value: 'daily', label: a.editor.repeatDaily },
    { value: 'weekly', label: a.editor.repeatWeekly },
    { value: 'biweekly', label: a.editor.repeatBiweekly },
    { value: 'monthly', label: a.editor.repeatMonthly },
    { value: 'yearly', label: a.editor.repeatYearly },
  ];
  const weekdayName = m.weekdays[weekdayOf(f.date) - 1] ?? '';
  const dayOfMonth = Number(f.date.slice(8, 10));
  // Herinneringen horen bij de reeks: gewijzigd = geen "alleen deze keer".
  const scopeAllowed: EditScope[] =
    repeatChanged() ||
    (initial.value && !sameReminders(f.reminders, initial.value.reminders)) ||
    (initial.value && rideDaysChanged(f, initial.value))
      ? ['this_and_following', 'all']
      : ['this', 'this_and_following', 'all'];

  /** Sjabloontegel; `dim` = verborgen sjabloon na "Alles tonen" (alleen het beeld gedimd). */
  const templateChip = (tpl: EventTemplate, dim: boolean) => (
    <button
      key={tpl.id}
      type="button"
      class={cx('template-chip', dim && 'is-hidden-option')}
      onClick={() => {
        const p = tpl.payload;
        iconTouched.value = true;
        const repeat = p.rrule
          ? repeatFromInput({ rrule: p.rrule }, form.value.date)
          : defaultRepeat(form.value.date);
        // Rijders per weekdag uit het sjabloon (review c-fs r3), alleen als de
        // herhaling op twee of meer weekdagen valt (anders de eenvoudige keuze).
        const days = formWeekdays({ repeat, date: form.value.date });
        const rideDays =
          days.length >= 2
            ? daysFromDetail({ roles: p.roles ?? {}, role_weekdays: p.role_weekdays }, days)
            : null;
        if (rideDays) more.value = true;
        const start = p.start_hhmm ?? form.value.start;
        const duration = p.duration_min ?? NEW_DURATION;
        lastDuration.current = duration;
        endClamped.value = false;
        patch({
          title: p.title,
          icon: templateIcon(tpl.name, p.icon, m.design.picto, m.design.pictoKeywords),
          category: p.category ?? null,
          allDay: p.all_day ?? false,
          start,
          end: hhmmOf(Math.min(minutesOf(start) + duration, 23 * 60 + 59)),
          ...(p.member_ids ? { member_ids: p.member_ids } : {}),
          location: p.location ?? '',
          notes: p.notes ?? '',
          travel: p.travel_minutes ?? null,
          alert: p.departure_alert_minutes ?? 0,
          repeat,
          dropoff: roleChoice(p.roles?.dropoff ?? null),
          pickup: roleChoice(p.roles?.pickup ?? null),
          rideDays,
        });
      }}
    >
      <Pictogram
        icon={templateIcon(tpl.name, tpl.payload.icon, m.design.picto, m.design.pictoKeywords)}
        label={tpl.name}
        size={24}
        decorative
      />
      <span>{tpl.name}</span>
    </button>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        variant="sheet"
        title={editing ? a.editor.editTitle : a.editor.newTitle}
        closeLabel={m.design.dialog.close}
        name="event-editor"
        expand={{
          expanded: full.value,
          edgeToggle: true,
          onToggle: toggleFull,
          expandLabel: a.editor.fullScreen,
          collapseLabel: a.editor.smallScreen,
        }}
        actions={
          <>
            <Button name="event-cancel" onPress={onClose}>
              {a.common.cancel}
            </Button>
            <Button
              name="event-create"
              type="submit"
              variant="primary"
              busy={busy.value}
              disabled={loading.value}
              onPress={() => void submit()}
            >
              {/* Zelfde woord op elke breedte, ook op de telefoon (ronde 3). */}
              {a.editor.save}
            </Button>
          </>
        }
      >
        {loading.value ? (
          <p class="muted">{a.common.loading}</p>
        ) : (
          <form
            key={openKey.current}
            class={cx('form-stack', full.value && 'editor-full')}
            data-name="event-editor-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            {notice.value ? (
              <Notice kind="warning" name="editor-notice">
                {notice.value}
              </Notice>
            ) : null}

            {!editing && templates.value.length > 0 ? (
              <div class="template-row" role="group" aria-label={a.editor.templates}>
                {templates.value.slice(0, 16).map((tpl) => templateChip(tpl, false))}
              </div>
            ) : null}
            {!editing ? (
              <HiddenOptions
                name="ev-templates"
                hidden={hiddenTemplates.value}
                badge={hiddenBadge.value}
                render={(items) => (
                  <div class="template-row" role="group" aria-label={a.editor.templates}>
                    {items.map((tpl) => templateChip(tpl, true))}
                  </div>
                )}
              />
            ) : null}

            <div class="editor-col form-stack" data-name="editor-col-main">
              <TextField
                id="ev-title"
                label={a.editor.title}
                value={f.title}
                onInput={(v) =>
                  patch({
                    title: v,
                    ...(iconTouched.value
                      ? {}
                      : {
                          icon: suggestPicto(
                            v,
                            m.design.picto,
                            AVATAR_IDS,
                            m.design.pictoKeywords,
                            hiddenPictoIds(pictoCatalog),
                          ),
                        }),
                  })
                }
                placeholder={a.editor.titlePlaceholder}
                error={errors.value.title}
                maxLength={120}
                autoFocus={!editing}
                required
              />
              <PictoField
                id="ev-icon"
                label={a.editor.picto}
                value={f.icon}
                onChange={(icon) => {
                  iconTouched.value = true;
                  patch({ icon });
                }}
                fallbackLabel={f.title}
              />
              <MemberPicker
                id="ev-members"
                label={a.editor.who}
                members={members}
                value={f.member_ids}
                onChange={(v) => patch({ member_ids: v })}
                allowEveryone
                error={errors.value.member_ids}
              />

              <div class="field-group" data-name="when">
                {/* Datum op een eigen rij, Van/Tot ernaast in gelijke kolommen (verzoek oprichter
                  2026-09-24: waarden werden afgekapt en het icoon stond over de tekst). */}
                <DateField
                  id="ev-date"
                  label={a.editor.date}
                  value={f.date}
                  onInput={(v) => patch({ date: v })}
                  error={errors.value.date}
                  required
                />
                {!f.allDay ? (
                  <FieldPair name="ev-times">
                    <TimeField
                      id="ev-start"
                      label={a.editor.start}
                      labels={a.timePicker}
                      value={f.start}
                      onInput={(v) => {
                        // Begintijd schuift: de duur blijft gelijk (lib/timeRange.ts).
                        const moved = endAfterStartChange(v, lastDuration.current);
                        endClamped.value = moved?.clamped ?? false;
                        if (!moved) keepDuration(v, form.value.end);
                        patch({ start: v, ...(moved ? { end: moved.end } : {}) });
                        clearTimeError('start', v);
                      }}
                      error={errors.value.start}
                      step={300}
                      required
                    />
                    <TimeField
                      id="ev-end"
                      label={a.editor.end}
                      labels={a.timePicker}
                      durationFrom={f.start}
                      value={f.end}
                      onInput={(v) => {
                        endClamped.value = false;
                        keepDuration(form.value.start, v);
                        patch({ end: v });
                        clearTimeError('end', v);
                      }}
                      error={errors.value.end}
                      hint={endClamped.value ? a.timePicker.endClamped : undefined}
                      step={300}
                      required
                    />
                  </FieldPair>
                ) : (
                  <DateField
                    id="ev-end-date"
                    label={a.editor.endDate}
                    value={f.endDate}
                    min={f.date}
                    onInput={(v) => patch({ endDate: v })}
                  />
                )}
                {/* Tijdkiezer: één tik voor de duur (Tot = Van + duur). */}
                {!f.allDay ? (
                  <DurationChips
                    id="ev-durations"
                    start={f.start}
                    end={f.end}
                    onPick={(v) => {
                      endClamped.value = false;
                      keepDuration(form.value.start, v);
                      patch({ end: v });
                      clearTimeError('end', v);
                    }}
                    labels={a.timePicker}
                  />
                ) : null}
                <Checkbox
                  id="ev-allday"
                  label={a.editor.allDay}
                  checked={f.allDay}
                  onChange={(v) => patch({ allDay: v })}
                />
              </div>

              <div class="field-group repeat-editor" data-name="repeat-editor">
                <Select
                  id="ev-repeat"
                  label={a.editor.repeat}
                  value={f.repeat.kind}
                  onChange={(v) => patchRepeat({ kind: v as RepeatKind })}
                  options={repeatOptions}
                />
                {f.repeat.kind === 'weekly' || f.repeat.kind === 'biweekly' ? (
                  <fieldset class="weekday-picker">
                    <legend class="field-label">{a.editor.onDays}</legend>
                    <div class="weekday-options">
                      {WEEKDAY_ORDER.map((code, i) => {
                        const on = f.repeat.weekdays.includes(code);
                        return (
                          <label key={code} class={cx('weekday-chip', on && 'is-checked')}>
                            <input
                              type="checkbox"
                              name={`ev-day-${code}`}
                              checked={on}
                              onChange={() => {
                                const next = on
                                  ? f.repeat.weekdays.filter((x) => x !== code)
                                  : [...f.repeat.weekdays, code];
                                patchRepeat({ weekdays: next.length > 0 ? next : [code] });
                              }}
                            />
                            <span aria-hidden="true">{m.weekdaysShort[i]}</span>
                            <span class="fb-sr-only">{m.weekdays[i]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ) : null}
                {f.repeat.kind === 'monthly' ? (
                  <Select
                    id="ev-monthly"
                    label={a.editor.monthlyHow}
                    value={f.repeat.monthly}
                    onChange={(v) => patchRepeat({ monthly: v as RepeatForm['monthly'] })}
                    options={[
                      { value: 'day', label: t(a.editor.monthlyDay, { day: dayOfMonth }) },
                      {
                        value: 'nth',
                        label: t(a.editor.monthlyNth, {
                          ordinal:
                            (a.editor.ordinal as Record<string, string>)[String(f.repeat.nth)] ??
                            '',
                          weekday: weekdayName,
                        }),
                      },
                      { value: 'last', label: a.editor.monthlyLast },
                    ]}
                  />
                ) : null}
                {f.repeat.kind === 'weekly' || f.repeat.kind === 'biweekly' ? (
                  <Select
                    id="ev-parity"
                    label={a.editor.parity}
                    value={f.repeat.parity}
                    onChange={(v) => patchRepeat({ parity: v as RepeatForm['parity'] })}
                    options={[
                      { value: 'all', label: a.editor.parityAll },
                      { value: 'even', label: a.editor.parityEven },
                      { value: 'odd', label: a.editor.parityOdd },
                    ]}
                  />
                ) : null}
                {f.repeat.kind !== 'none' ? (
                  <FieldPair min={f.repeat.ends === 'until' ? 176 : 140} name="ev-ends-row">
                    <Select
                      id="ev-ends"
                      label={a.editor.ends}
                      value={f.repeat.ends}
                      onChange={(v) => patchRepeat({ ends: v as RepeatForm['ends'] })}
                      options={[
                        { value: 'never', label: a.editor.endsNever },
                        { value: 'count', label: a.editor.endsCount },
                        { value: 'until', label: a.editor.endsUntil },
                      ]}
                    />
                    {f.repeat.ends === 'count' ? (
                      <TextField
                        id="ev-count"
                        label={a.editor.count}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={1000}
                        value={String(f.repeat.count)}
                        onInput={(v) => patchRepeat({ count: Number(v) || 1 })}
                      />
                    ) : null}
                    {f.repeat.ends === 'until' ? (
                      <DateField
                        id="ev-until"
                        label={a.editor.until}
                        min={f.date}
                        value={f.repeat.until}
                        onInput={(v) => patchRepeat({ until: v })}
                      />
                    ) : null}
                  </FieldPair>
                ) : null}
                {summary ? (
                  <div class="repeat-summary" data-name="repeat-summary" aria-live="polite">
                    <Icon id="repeat" size={18} />
                    <div>
                      <p class="repeat-summary-text">{summary}</p>
                      {preview.value && preview.value.next_dates.length > 0 ? (
                        <p class="repeat-summary-next">
                          {a.editor.nextDates}:{' '}
                          {preview.value.next_dates
                            .slice(0, 4)
                            .map((d) => formatDateKeyShort(d, m))
                            .join(' · ')}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {(preview.value?.warnings ?? []).map((w) => (
                  <Notice key={w} kind="info">
                    {w}
                  </Notice>
                ))}
              </div>

              {conflicts.length > 0 ? (
                <Notice kind="warning" name="conflict-notice">
                  {conflicts.slice(0, 2).map((c) => (
                    <span key={c.occurrence_id} class="block">
                      {t(a.editor.conflictNotice, {
                        count: membersOf(c.member_ids, members).length,
                        names: joinNames(
                          membersOf(c.member_ids, members).map((x) => x.display_name),
                        ),
                        title: c.title,
                        time: c.start_hhmm
                          ? `${formatDateKeyShort(c.date, m)} ${c.start_hhmm}`
                          : formatDateKeyShort(c.date, m),
                      })}
                    </span>
                  ))}
                </Notice>
              ) : null}

              <FixedActivityEditor
                seriesId={editing ? editing.series_id : null}
                recurring={f.repeat.kind !== 'none'}
                date={f.date}
                handleRef={fixedRef}
              />
            </div>
            <div class="editor-col form-stack" data-name="editor-col-more">
              {/* 0029: alleen met de modules Herinneringen, Brengen en halen en Reistijd (de server
                  negeert de velden ook als een module uit staat; er gaat niets verloren). */}
              {moduleOn('reminders') ? (
                <ReminderEditor
                  value={f.reminders}
                  onChange={(reminders) => patch({ reminders })}
                  event={{
                    title: f.title,
                    icon: f.icon,
                    category: f.category,
                    start: f.start,
                    allDay: f.allDay,
                    memberIds: f.member_ids,
                    baseMemberIds: initial.value ? initial.value.member_ids : null,
                    recurring: !!initial.value && initial.value.repeat.kind !== 'none',
                    choiceMemberIds: reminderChoices(),
                  }}
                  members={members}
                  flushRef={reminderFlush}
                />
              ) : null}

              {/* Volledig scherm: alle instellingen in beeld, geen "Meer opties" nodig. */}
              {!full.value ? (
                <button
                  type="button"
                  class="more-toggle"
                  aria-expanded={more.value ? 'true' : 'false'}
                  onClick={() => (more.value = !more.value)}
                  data-name="editor-more"
                >
                  <Icon id={more.value ? 'chevron-up' : 'chevron-down'} size={18} />
                  {more.value ? a.editor.less : a.editor.more}
                </button>
              ) : null}

              {more.value || full.value ? (
                <div class="form-stack">
                  {/* 0034: "Waar" eerst: met een gekozen plek rekent de app de reistijd uit. */}
                  {/* fix/afspraakformulier: altijd het veld met de schakelaar; met de module uit
                      staat die grijs en zegt het veld waarom (er gaat dan niets naar de server). */}
                  <PlaceField
                    id="ev-location"
                    label={a.editor.location}
                    value={f.location}
                    onInput={(v) => patch({ location: v })}
                    seriesId={editing ? editing.series_id : null}
                    handleRef={placeRef}
                    onAuto={onPlaceAuto}
                    available={moduleOn('place_search')}
                  />
                  {moduleOn('rides') || moduleOn('travel_time') ? (
                    <div class="field-group">
                      {moduleOn('rides') ? (
                        <>
                          <p class="field-label">{a.editor.rides}</p>
                          <p class="field-hint">{a.editor.ridesHint}</p>
                          <RidesEditor
                            form={f}
                            initial={initial.value}
                            members={members}
                            onChange={patch}
                          />
                        </>
                      ) : null}
                      {moduleOn('travel_time') ? (
                        // Review g3-ux r2: elk een eigen rij over de hele breedte; naast elkaar kapte
                        // een <select> "Automatisch (10 min)" af (tr, ar en op de telefoon nog meer).
                        <div class="field-row field-stack">
                          <div class="field-grow">
                            {/* Review g3-fs r1: "Automatisch" (de berekende tijd van de plek) en
                                "Geen" zijn twee keuzes; "Geen" wint altijd van de berekende tijd. */}
                            <Select
                              id="ev-travel"
                              label={a.editor.travel}
                              value={
                                travelOwn.value
                                  ? TRAVEL_OWN
                                  : f.travel === null
                                    ? autoTravelShown
                                      ? TRAVEL_AUTO
                                      : '0'
                                    : String(f.travel)
                              }
                              onChange={(v) => {
                                travelOwn.value = v === TRAVEL_OWN;
                                if (v === TRAVEL_OWN) return;
                                patch({ travel: v === TRAVEL_AUTO ? null : Number(v) });
                              }}
                              options={[
                                ...(autoTravelShown
                                  ? [
                                      {
                                        value: TRAVEL_AUTO,
                                        label:
                                          placeAuto.value.minutes !== null
                                            ? t(a.editor.travelAutoWith, {
                                                minutes: placeAuto.value.minutes,
                                              })
                                            : a.editor.travelAuto,
                                      },
                                    ]
                                  : []),
                                ...travelOptions(f.travel).map((n) => ({
                                  value: String(n),
                                  label: n === 0 ? a.editor.travelNone : formatDuration(n, a),
                                })),
                                { value: TRAVEL_OWN, label: a.editor.travelOwn },
                              ]}
                            />
                            {travelOwn.value ? (
                              <TextField
                                id="ev-travel-own"
                                label={a.editor.travelOwnLabel}
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={TRAVEL_MAX}
                                value={f.travel === null ? '' : String(f.travel)}
                                onInput={(v) => {
                                  const n = Number(v);
                                  if (v.trim() !== '' && Number.isFinite(n))
                                    patch({
                                      travel: Math.max(0, Math.min(TRAVEL_MAX, Math.round(n))),
                                    });
                                }}
                                hint={
                                  f.travel !== null && f.travel >= 60
                                    ? formatDuration(f.travel, a)
                                    : undefined
                                }
                              />
                            ) : null}
                            {/* 0056 (oprichter 1 okt): dezelfde reistijd ook terug, ná de afspraak. */}
                            {f.travel !== 0 && (f.travel !== null || autoTravelShown) ? (
                              <Checkbox
                                id="ev-travel-back"
                                label={a.editor.travelBack}
                                hint={a.editor.travelBackHint}
                                checked={f.travelBack}
                                onChange={(v) => patch({ travelBack: v })}
                              />
                            ) : null}
                          </div>
                          <div class="field-grow">
                            <Select
                              id="ev-alert"
                              label={a.editor.departureAlert}
                              value={String(f.alert)}
                              onChange={(v) => patch({ alert: Number(v) })}
                              options={ALERT_OPTIONS.map((n) => ({
                                value: String(n),
                                label:
                                  n === 0
                                    ? a.editor.departureNone
                                    : t(a.editor.departureBefore, { count: n }),
                              }))}
                              hint={a.editor.departureAlertHint}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <label class="textarea-field" for="ev-notes">
                    <span class="field-label">{a.editor.notes}</span>
                    <textarea
                      id="ev-notes"
                      class="textarea"
                      rows={3}
                      maxLength={2000}
                      placeholder={a.editor.notesPlaceholder}
                      value={f.notes}
                      onInput={(e) =>
                        patch({ notes: (e.currentTarget as HTMLTextAreaElement).value })
                      }
                    />
                  </label>
                </div>
              ) : null}
            </div>
          </form>
        )}
      </Dialog>

      {editing ? (
        <ScopeDialog
          open={scopeOpen.value}
          mode="edit"
          dateLabel={formatDateKeyLong(editing.date ?? f.date, m)}
          allowed={scopeAllowed}
          busy={busy.value}
          onClose={() => (scopeOpen.value = false)}
          onChoose={(scope) => void saveWithScope(scope)}
        />
      ) : null}

      <Dialog
        open={open && discardOpen.value}
        onClose={() => (discardOpen.value = false)}
        title={a.editor.discardTitle}
        description={a.editor.discardBody}
        closeLabel={m.design.dialog.close}
        name="discard-dialog"
        actions={
          <>
            <Button name="discard-keep" onPress={() => (discardOpen.value = false)}>
              {a.editor.discardKeep}
            </Button>
            <Button
              name="discard-confirm"
              variant="danger"
              onPress={() => {
                discardOpen.value = false;
                onClose();
              }}
            >
              {a.editor.discardConfirm}
            </Button>
          </>
        }
      />

      <Dialog
        open={orphanScope.value !== null}
        onClose={() => (orphanScope.value = null)}
        title={a.editor.orphansTitle}
        description={a.editor.orphansBody}
        closeLabel={m.design.dialog.close}
        name="orphans-dialog"
        actions={
          <>
            <Button name="orphans-cancel" onPress={() => (orphanScope.value = null)}>
              {a.common.cancel}
            </Button>
            <Button
              name="orphans-confirm"
              variant="primary"
              busy={busy.value}
              onPress={() => {
                const scope = orphanScope.value;
                if (scope) void saveWithScope(scope, orphanChoice.value);
              }}
            >
              {a.scope.confirm}
            </Button>
          </>
        }
      >
        <RadioList
          name="orphans"
          label={a.editor.orphansTitle}
          value={orphanChoice.value}
          onChange={(v) => (orphanChoice.value = v)}
          options={[
            { value: 'keep_as_event', label: a.editor.orphansKeep },
            { value: 'delete', label: a.editor.orphansDelete },
          ]}
        />
      </Dialog>
    </>
  );
}

// Voor andere schermen: standaarddag + volgend heel uur voor "snel een afspraak".
export function quickTarget(date: string, start: string, memberIds?: MemberIds): EditorTarget {
  return { kind: 'new', date, start, ...(memberIds ? { memberIds } : {}) };
}
